import { useForm } from "react-hook-form";
import { useCreateCategoryMutation, useUpdateCategoryMutation } from "../../../../../../API/authApi";
import { ICreateCategory, IUpdateCategory, } from "../../../../../../Interfaces/request-interfaces";
import { useEffect, useRef, useState } from "react";
import { CreateFormTypes, ImageTypes, MenuCategoryTypes } from "../../../../../../Interfaces/componentTypes";
import { ICreateCategoryProps } from "./createCategoryDialog";
import useUploadFile from "../../../../../../Components/FileUploader/fileUploader-hooks";
import { ICreateCategoryForm } from "../../../../../../Interfaces/formValue-interfaces";
import useLanguagesHooks from "../../../../../../Components/Languages/setLanguage-hooks";
import { useToastify } from "../../../../../../Hooks/hooks";

const useCreateCategory = (props: ICreateCategoryProps) => {
    const { t } = useLanguagesHooks();
    const [activeForm, setActiveForm] = useState<CreateFormTypes>(CreateFormTypes.CREATE);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [createCategory, { data: categoryResponce, isLoading: createLoading, isSuccess: createSuccess, isError: createError }] = useCreateCategoryMutation();
    const [updateCreatedCategory, { isLoading: updateCreateLoading, isSuccess: updateSuccess, isError: updateError }] = useUpdateCategoryMutation();
    const { showToastSuccess, showToastError } = useToastify();
    const {
        register,
        control,
        formState: { errors, isValid },
        setValue,
        getValues,
        watch
    } = useForm<ICreateCategoryForm>({ mode: 'all' });
    const { imageUrl, handleFileInputChange, ImageResponce, imageLoading } = useUploadFile(ImageTypes.CATEGORY);
    const loading = createLoading;
    const createValidation = isValid && imageUrl;
    const watchedFields = watch();
    const dialogRef = useRef<HTMLDivElement | null>(null);

    const onCloseDialog = props.onClose;

    function formatName(name: string) {
        if (typeof name !== 'string') {
            throw new Error('Input must be a string');
        }
        const trimmedString = name.trim();
        if (trimmedString.length === 0) {
            return '';
        }
        return trimmedString.charAt(0).toUpperCase() + trimmedString.slice(1);
    };

    useEffect(() => {
        if (categoryResponce) {
            const initialValues = {
                name_hy: categoryResponce.name_hy || "",
                name_en: categoryResponce.name_en || "",
                name_ru: categoryResponce.name_ru || "",
            };

            const relevantWatchedFields = {
                name_hy: watchedFields.name_hy,
                name_en: watchedFields.name_en,
                name_ru: watchedFields.name_ru,
            };

            const hasChanged = Object.keys(initialValues).some(
                (key) => relevantWatchedFields[key as keyof typeof initialValues] !== initialValues[key as keyof typeof initialValues]
            );
            setIsFormChanged(hasChanged);
        }
    }, [watchedFields, categoryResponce]);

    useEffect(() => {
        if (createSuccess && categoryResponce) {
            props.cb()
            setValue("name_hy", categoryResponce.name_hy);
            setValue("name_en", categoryResponce.name_en);
            setValue("name_ru", categoryResponce.name_ru);
            if (dialogRef.current) {
                dialogRef!.current.scrollTop = 0;
            }
            setActiveForm(CreateFormTypes.SAVE);
        } else if (createError) {
            showToastError("Something went wrong")
        }
    }, [createSuccess, categoryResponce, createError]);

    useEffect(() => {
        if (updateSuccess) {
            showToastSuccess(t('Toast.Success.Category_Create'));
            props.cb();
            props.onClose();
        } else if (updateError) {
            showToastError(t('Toast.Error.Common'))
        }
    }, [updateSuccess, updateError]);

    const onCreate = () => {
        const payload: ICreateCategory = {
            company_id: props.company_id,
            default_image: ImageResponce?.src!,
            menu_type: getValues().menu_type.id as MenuCategoryTypes,
            name: formatName(getValues().name),
        }
        createCategory(payload)
    };

    const onUpdate = () => {
        const payload: IUpdateCategory = {
            category_id: categoryResponce?.id!,
            default_image: ImageResponce?.src!,
            menu_type: getValues().menu_type.id as MenuCategoryTypes,
            name_en: getValues().name_en,
            name_ru: getValues().name_ru,
            name_hy: getValues().name_hy,
        };

        updateCreatedCategory(payload)
    };

    return {
        errors,
        loading,
        control,
        imageUrl,
        dialogRef,
        activeForm,
        imageLoading,
        createLoading,
        isFormChanged,
        categoryResponce,
        createValidation,
        updateCreateLoading,
        onCreate,
        register,
        onUpdate,
        onCloseDialog,
        handleFileInputChange,
    }
};

export default useCreateCategory